import { useState, useEffect } from 'react';
import { useServices } from '../hooks/useServices';
import type { ServiceCategory, ServiceWithChecklist, Service } from '../types';

interface UpdateServiceProps {
  service: Service;
  onSuccess?: (service: Service) => void;
  onCancel?: () => void;
}

export function UpdateService({ service, onSuccess, onCancel }: UpdateServiceProps) {
  const { updateService } = useServices();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Omit<ServiceWithChecklist, 'id'>>({
    name: service.name,
    description: service.description,
    basePrice: service.basePrice,
    duration: service.duration,
    category: service.category,
    addOns: service.addOns,
  });

  // Update form data when service prop changes
  useEffect(() => {
    setFormData({
      name: service.name,
      description: service.description,
      basePrice: service.basePrice,
      duration: service.duration,
      category: service.category,
      addOns: service.addOns,
    });
  }, [service]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const updatedService = await updateService(service.id, formData);
      onSuccess?.(updatedService);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update service');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOnChange = (addOn: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      addOns: checked 
        ? [...prev.addOns, addOn]
        : prev.addOns.filter(item => item !== addOn)
    }));
  };

  const availableAddOns = [
    'Inside Oven',
    'Inside Fridge',
    'Inside Windows',
    'Garage Cleaning',
    'Basement Cleaning'
  ];

  return (
    <div className="max-w-2xl mx-auto bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">Update Service</h2>
      </div>
      
      <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Service Name *
          </label>
          <input
            type="text"
            id="name"
            required
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            id="description"
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="basePrice" className="block text-sm font-medium text-gray-700">
              Base Price ($) *
            </label>
            <input
              type="number"
              id="basePrice"
              required
              min="0"
              step="0.01"
              value={formData.basePrice}
              onChange={(e) => setFormData(prev => ({ ...prev, basePrice: parseFloat(e.target.value) || 0 }))}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label htmlFor="duration" className="block text-sm font-medium text-gray-700">
              Duration (minutes) *
            </label>
            <input
              type="number"
              id="duration"
              required
              min="1"
              value={formData.duration}
              onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) || 60 }))}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700">
            Category *
          </label>
          <select
            id="category"
            required
            value={formData.category}
            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as ServiceCategory }))}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
            <option value="deep-cleaning">Deep Cleaning</option>
            <option value="specialized">Specialized</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Available Add-ons
          </label>
          <div className="space-y-2">
            {availableAddOns.map((addOn) => (
              <label key={addOn} className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.addOns.includes(addOn)}
                  onChange={(e) => handleAddOnChange(addOn, e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">{addOn}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? 'Updating...' : 'Update Service'}
          </button>
        </div>
      </form>
    </div>
  );
}